import { Rating, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import pluralize from "pluralize";

interface RatingSummaryProps {
  rating: number;
  ratingsCount: number;
}

/**
 * Displays rating information with a vertical divider:
 * - Left side: rating value (e.g., "4.6") and "out of 5"
 * - Right side: star rating and review count
 * Shows "Not yet rated" when there are no ratings.
 */
export function RatingSummary(props: RatingSummaryProps) {
  const { rating, ratingsCount } = props;

  if (ratingsCount === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={4}>
        <Rating readOnly value={0} size="medium" />
        <Text bold variant="body2" color={(theme) => theme.palette.text.secondary}>
          Not yet rated
        </Text>
      </Stack>
    );
  }

  const displayRating = rating.toFixed(1);
  const reviewText = `${ratingsCount} ${pluralize("rating", ratingsCount)}`;

  return (
    <Stack direction="row" alignItems="center" spacing={5}>
      <Stack alignItems="center" spacing={0}>
        <Text semibold variant="h3" color={(theme) => theme.palette.text.primary}>
          {displayRating}
        </Text>
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          out of 5
        </Text>
      </Stack>

      <Box
        sx={(theme) => ({
          width: "1px",
          alignSelf: "stretch",
          backgroundColor: theme.palette.border?.primary,
        })}
      />

      <Stack alignItems="flex-start" spacing={1}>
        <Rating readOnly value={rating} size="medium" />
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          {reviewText}
        </Text>
      </Stack>
    </Stack>
  );
}
